import { formatAmount } from "../componenets/foodDictionary/foodDictionaryNutrientUtils";
import {
  Box,
  CircularProgress,
  InputBase,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Search as SearchIcon,
  MenuBook as DictIcon,
  VerifiedOutlined as CertifiedIcon,
  RestaurantMenu as ServingsIcon,
} from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  searchFoodDictionary,
  createFoodDictionary as createSystemFood,
} from "../services/system/FoodDictionaryService";
import {
  searchCatalog as searchCompanyCatalog,
  createFood as createCompanyFood,
} from "../services/FoodDictionaryService";
import FoodDictionaryCreateDialog from "../componenets/foodDictionary/FoodDictionaryCreateDialog";
import { getCompanies } from "../services/system/CompanyService";
import { FilterDropdown, StatusChip, Pagination, PrimaryButton, SecondaryButton } from "../componenets/ui";
import { useAuth } from "../contexts/AuthContext";
import { t, macro as M } from "../componenets/dashboard/coach/coachTokens";
import { KPI } from "../componenets/dashboard/coach/KpiRow";
import { FOOD_SCOPE, getLabel } from "../enums/FoodDictionaryEnums";

const PAGE_SIZE = 8;

// The catalog now spans three tenancies, so there are two orthogonal lenses on it: `scope` says
// which tenancy to look at, and `company` picks one company out of the company-owned foods. They
// compose — SYSTEM plus a company is an empty set by definition, so choosing a company narrows the
// scope filter to COMPANY rather than fighting it.
const SCOPE_OPTS = [
  { value: "ALL", label: "הכול" },
  ...FOOD_SCOPE.map((o) => ({ value: o.value, label: o.name })),
];

const CERTIFIED_OPTS = [
  { value: "ALL", label: "הכול" },
  { value: "true", label: "מאושר" },
  { value: "false", label: "לא מאושר" },
];

const td = { padding: "10px 12px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", color: t.text, verticalAlign: "middle" };
const th = { padding: "10px 12px", fontSize: "10.5px", fontWeight: 700, color: t.t3, borderBottom: `1px solid ${t.border}`,
  background: t.bg, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".06em" };

const FoodDictionaryScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSuperAdmin } = useAuth();

  const [creating, setCreating] = useState(false);

  // A system admin browses the whole catalog and can filter it by scope and company. Everyone else
  // gets the company-scoped endpoint, which returns their own company's foods plus the shared system
  // ones and nothing else — the same split the server enforces, mirrored here so the screen only ever
  // asks for what the caller is allowed to see.
  const loadPage = useCallback(
    (opts) => (isSuperAdmin() ? searchFoodDictionary(opts) : searchCompanyCatalog(opts)),
    [isSuperAdmin]
  );
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);

  const [f, setF] = useState({
    foods: [],
    total: 0,
    keyword: query.get("keyword") || "",
    ownOnly: query.get("ownOnly") === "true",
    certified: query.get("certified") || null,
    scope: query.get("scope") || null,
    companyId: query.get("companyId") || null,
    page: +(query.get("page") || 0),
    loading: true,
  });

  const [searchInput, setSearchInput] = useState(f.keyword);
  const debounceRef = useRef(null);
  const handleSearch = useCallback((val) => {
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => set({ keyword: val }), 300);
  }, []);

  const sync = (ns) => {
    const p = new URLSearchParams();
    if (ns.keyword) p.set("keyword", ns.keyword);
    if (ns.ownOnly) p.set("ownOnly", "true");
    if (ns.certified) p.set("certified", ns.certified);
    if (ns.scope) p.set("scope", ns.scope);
    if (ns.companyId) p.set("companyId", ns.companyId);
    if (ns.page > 0) p.set("page", ns.page);
    navigate(p.toString() ? `${location.pathname}?${p}` : location.pathname, { replace: true });
  };

  const set = (patch) => { const ns = { ...f, page: 0, ...patch }; setF(ns); sync(ns); };

  useEffect(() => {
    const count = (opts = {}) =>
      loadPage({ page: 0, size: 1, ...opts }).then((r) => r.data.totalElements).catch(() => null);
    Promise.all([
      count(),
      count({ certified: true }),
    ]).then(([total, certified]) => setStats({ total, certified }));
  }, [isSuperAdmin]);

  /**
   * Only the newest search may write to the screen.
   *
   * <p>The debounce keeps a request per keystroke from being sent; it does not keep two in flight
   * from landing out of order. Typing "מ", pausing past the debounce, then finishing "מלפ" sends
   * both — and the one-letter search is the slow one, because it matches thousands of rows. It comes
   * back last and overwrites the narrow result, leaving the box reading "מלפ" beside 3,898 foods that
   * merely contain "מ". The keyword looks ignored; the request that produced it just finished second.
   *
   * <p>A counter rather than an AbortController: the generated SDK does not thread a signal through,
   * and the fix needed is "ignore the answer", not "cancel the question".
   */
  const requestSeq = useRef(0);

  useEffect(() => {
    const seq = ++requestSeq.current;
    const isStale = () => seq !== requestSeq.current;

    setF((p) => ({ ...p, loading: true }));
    loadPage({
      page: f.page,
      size: PAGE_SIZE,
      keyword: f.keyword,
      certified: f.certified === null ? null : f.certified === "true",
      scope: f.scope,
      companyId: f.companyId,
      ownOnly: f.ownOnly,
    })
      .then((r) => {
        if (isStale()) return;
        setF((p) => ({ ...p, foods: r.data.content, total: r.data.totalElements, loading: false }));
      })
      // A stale failure must not clear the spinner either — the request still running is the one the
      // person is waiting on.
      .catch(() => { if (!isStale()) setF((p) => ({ ...p, loading: false })); });
  }, [f.page, f.keyword, f.certified, f.scope, f.companyId, f.ownOnly, isSuperAdmin]);

  useEffect(() => {
    // The company filter is a system-admin tool; nobody else has more than one company to pick from.
    if (!isSuperAdmin()) return;
    // Only enabled companies — a disabled tenant's foods are not something to review by company.
    getCompanies("", true)
      .then((r) => setCompanies(r?.data ?? r ?? []))
      .catch(() => setCompanies([]));
  }, [isSuperAdmin]);

  const companyOpts = useMemo(
    () => [{ value: "ALL", label: "כל החברות" },
           ...companies.map((c) => ({ value: String(c.id), label: c.name || `#${c.id}` }))],
    [companies],
  );

  // Which endpoint answers decides the scope: a system admin's food is platform-wide, a company
  // admin's belongs to their company. Neither is asked to choose, and neither could override it.
  const handleCreate = async (payload) => {
    if (isSuperAdmin()) {
      await createSystemFood(payload);
    } else {
      // The company create answers with a {success, message} envelope rather than throwing, because
      // the meal-plan dialogs branch on it. Turn a failure back into a throw so the dialog can show it
      // instead of closing as though the food had been saved.
      const res = await createCompanyFood(payload);
      if (res && res.success === false) {
        throw new Error(res.message);
      }
    }
    set({ page: 0 });
  };

  const num = (v) => (v == null ? "—" : v.toLocaleString());
  const pages = Math.ceil(f.total / PAGE_SIZE);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "18px" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography sx={{ fontSize: { xs: "22px", md: "26px" }, fontWeight: 800, color: t.text, lineHeight: 1.2 }}>
            מאגר מזון
          </Typography>
          <Typography sx={{ fontSize: "13px", color: t.t2, mt: 0.25 }}>קטלוג מזון ציבורי — מנות, מאקרו ומיקרונוטריינטים</Typography>
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: "14px" }}>
        <KPI icon={<DictIcon sx={{ fontSize: 22 }} />} iconBg={t.greenLight} iconColor={t.green}
             value={num(stats?.total)} label="סה״כ פריטים" onClick={() => set({ certified: null })} />
        <KPI icon={<CertifiedIcon sx={{ fontSize: 22 }} />} iconBg={t.blueLight} iconColor={t.blue}
             value={num(stats?.certified)} label="מאושרים" onClick={() => set({ certified: "true" })} />
        <KPI icon={<ServingsIcon sx={{ fontSize: 22 }} />} iconBg={t.amberLight} iconColor="#D97706"
             value={num(stats != null && stats.total != null && stats.certified != null ? stats.total - stats.certified : null)}
             label="לא מאושרים" onClick={() => set({ certified: "false" })} />
      </Box>

      <Box sx={{ background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r, boxShadow: t.sh, overflow: "hidden" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "7px", p: "10px 14px", borderBottom: `1px solid ${t.border}`, background: t.bg, flexWrap: "wrap", direction: "rtl" }}>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "12px", fontWeight: 700, color: t.greenText, background: t.greenLight, border: "1px solid rgba(1,187,167,.2)", px: "12px", py: "4px", borderRadius: "20px" }}>
            <DictIcon sx={{ fontSize: 14 }} /> {f.total} פריטים
          </Box>
          <Box sx={{ position: "relative" }}>
            <SearchIcon sx={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: t.t3, pointerEvents: "none" }} />
            <InputBase value={searchInput} onChange={(e) => handleSearch(e.target.value)} placeholder="חיפוש לפי שם או ברקוד..."
              sx={{ width: 220, p: "6px 10px 6px 30px", border: `1.5px solid ${t.border}`, borderRadius: t.rs, background: t.white, fontSize: "12px",
                "&:focus-within": { borderColor: t.green, boxShadow: "0 0 0 3px rgba(1,187,167,.08)" } }} />
          </Box>
          <FilterDropdown label="אישור" value={f.certified || "ALL"} onChange={(v) => set({ certified: v === "ALL" ? null : v })} options={CERTIFIED_OPTS} minWidth={130} />
          {/* A company admin's list is system foods plus their own; this drops the system half so they
              can see what their company actually added. Not shown to a system admin, who has the scope
              filter for the same job and no single company to call "mine". */}
          {!isSuperAdmin() && (
            <SecondaryButton
              onClick={() => set({ ownOnly: !f.ownOnly })}
              sx={f.ownOnly
                ? { borderColor: t.green, color: t.green, background: t.greenLight }
                : undefined}
            >
              {f.ownOnly ? "כל המזונות" : "המזונות של החברה שלי"}
            </SecondaryButton>
          )}

          <Box sx={{ ml: "auto" }}>
            <PrimaryButton onClick={() => setCreating(true)}>מזון חדש</PrimaryButton>
          </Box>

          {/* Scope and company are system-admin tools. A company admin's results are already limited to
              their own company plus the system catalog, so a scope picker would offer them filters for
              things they cannot see and a company picker would have exactly one entry. */}
          {isSuperAdmin() && (
            <>
              <FilterDropdown label="היקף" value={f.scope || "ALL"} onChange={(v) => set({ scope: v === "ALL" ? null : v, companyId: null })} options={SCOPE_OPTS} minWidth={130} />
              {/* Picking a company only makes sense among company-owned foods, so it sets the scope
                  too rather than silently returning nothing next to a SYSTEM filter. */}
              <FilterDropdown label="חברה" value={f.companyId || "ALL"}
                onChange={(v) => set({ companyId: v === "ALL" ? null : v, scope: v === "ALL" ? f.scope : "COMPANY" })}
                options={companyOpts} minWidth={170} />
            </>
          )}
        </Box>

        {f.loading && !f.foods.length ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}><CircularProgress size={32} sx={{ color: t.green }} /></Box>
        ) : !f.foods.length ? (
          <Box sx={{ textAlign: "center", py: 6, color: t.t3 }}><Typography>אין פריטים במאגר</Typography></Box>
        ) : (
          <>
            <TableContainer sx={{ overflowY: "auto", maxHeight: 540, direction: "rtl" }}>
              <Table stickyHeader sx={{ tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...th, textAlign: "right", width: "26%" }}>שם</TableCell>
                    <TableCell sx={{ ...th, textAlign: "center", width: "14%", display: { xs: "none", md: "table-cell" } }}>מותג</TableCell>
                    <TableCell sx={{ ...th, textAlign: "center", width: "10%", display: { xs: "none", md: "table-cell" } }}>קלוריות</TableCell>
                    <TableCell sx={{ ...th, textAlign: "center", width: "9%", display: { xs: "none", md: "table-cell" } }}>חלבון</TableCell>
                    <TableCell sx={{ ...th, textAlign: "center", width: "10%", display: { xs: "none", lg: "table-cell" } }}>מנות</TableCell>
                    <TableCell sx={{ ...th, textAlign: "center", width: "10%", display: { xs: "none", md: "table-cell" } }}>היקף</TableCell>
                    <TableCell sx={{ ...th, textAlign: "center", width: "9%", display: { xs: "none", md: "table-cell" } }}>מאושר</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {f.foods.map((food) => {
                    const nv = food.defaultServingNutrition;
                    return (
                      <TableRow key={food.id} onClick={() => navigate(`/app/food-dictionary/${food.id}`)}
                        sx={{ cursor: "pointer", "&:hover td": { background: t.greenLight } }}>
                        <TableCell sx={{ ...td, textAlign: "right" }}>
                          <Typography sx={{ fontSize: "12.5px", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{food.name}</Typography>
                        </TableCell>
                        <TableCell sx={{ ...td, textAlign: "center", display: { xs: "none", md: "table-cell" } }}>{food.brand || "—"}</TableCell>
                        <TableCell sx={{ ...td, textAlign: "center", display: { xs: "none", md: "table-cell" } }}>{nv?.calories ?? "—"}</TableCell>
                        <TableCell sx={{ ...td, textAlign: "center", display: { xs: "none", md: "table-cell" }, color: M.protein.text, fontWeight: 600 }}>
                          {formatAmount(nv?.protein)}
                        </TableCell>
                        <TableCell sx={{ ...td, textAlign: "center", display: { xs: "none", lg: "table-cell" } }}>
                          {food.servings?.length ?? "—"}
                        </TableCell>
                        <TableCell sx={{ ...td, textAlign: "center", display: { xs: "none", md: "table-cell" } }}>{getLabel(FOOD_SCOPE, food.scope)}</TableCell>
                        <TableCell sx={{ ...td, textAlign: "center", display: { xs: "none", md: "table-cell" } }}>
                          <StatusChip label={food.certified ? "כן" : "לא"} variant={food.certified ? "success" : "error"} size="small" />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {pages > 1 && (
              <Pagination currentPage={f.page} totalPages={pages} totalItems={f.total} itemsPerPage={PAGE_SIZE}
                onPageChange={(p) => { const ns = { ...f, page: p }; setF(ns); sync(ns); }} />
            )}
          </>
        )}
      </Box>

      <FoodDictionaryCreateDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreate={handleCreate}
      />
    </Box>
  );
};

export default FoodDictionaryScreen;
